import { useState } from 'react';
import { useAuthContext } from 'contexts/authContext';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Modal from '@mui/material/Modal';
import Paper from '@mui/material/Paper';
import Alert from '@mui/material/Alert';
import { resetPassword } from '../API/v1/ResetPassword';

function Login() {
  const { login, isAuthenticated } = useAuthContext();
  const theme = useTheme();
  const [email, setEmail] = useState('');
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [errorResetPassword, setErrorResetPassword] = useState(null);
  const [successResetPassword, setSuccessResetPassword] = useState(null);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [loginLocked, setLoginLocked] = useState(false);
  const [resetPasswordLocked, setResetPasswordLocked] = useState(false);

  const handleInputEmailChange = (event) => setEmail(event.target.value);
  const handleInputRecoveryEmailChange = (event) => setRecoveryEmail(event.target.value);
  const handleInputPasswordChange = (event) => setPassword(event.target.value);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (loginLocked) return;
    setLoginLocked(true);
    setError(null);
    await login(email, password);
    if (!isAuthenticated) {
      setError('Email o contraseña incorrectos');
    }
    setLoginLocked(false);
  };

  const handleOpenModal = () => setModalIsOpen(true);
  const handleCloseModal = () => {
    setModalIsOpen(false);
    setErrorResetPassword(null);
    setSuccessResetPassword(null);
  };

  const handleResetPassword = async () => {
    if (resetPasswordLocked) return;
    setResetPasswordLocked(true);
    var resetPasswordResult = await resetPassword(recoveryEmail);
    if (resetPasswordResult === null) {
      setErrorResetPassword('Error al enviar el correo de recuperación');
      setSuccessResetPassword(null);
    } else {
      setErrorResetPassword(null);
      setSuccessResetPassword('Revisa tu correo electrónico');
    }
    setResetPasswordLocked(false);
  };

  const isDark = theme.palette.mode === 'dark';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        background: isDark
          ? 'radial-gradient(ellipse at 60% 0%, rgba(0,7,255,0.12) 0%, transparent 60%), radial-gradient(ellipse at 20% 100%, rgba(75,80,255,0.08) 0%, transparent 50%), #0A0A14'
          : 'radial-gradient(ellipse at 60% 0%, rgba(0,7,255,0.06) 0%, transparent 60%), #F4F6FB',
      }}
    >
      <Paper
        elevation={isDark ? 4 : 2}
        sx={{
          p: { xs: 3, sm: 5 },
          width: '100%',
          maxWidth: 420,
          borderRadius: 4,
          border: isDark
            ? '1px solid rgba(255,255,255,0.07)'
            : '1px solid rgba(0,7,255,0.08)',
          backgroundColor: 'background.paper',
        }}
      >
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
          <img
            src={isDark
              ? 'https://eu2.contabostorage.com/68e1ac55689b4b99a9a2ccd407d95b2a:branding-absor/logos/absor/v1/complete_white.svg'
              : 'https://eu2.contabostorage.com/68e1ac55689b4b99a9a2ccd407d95b2a:branding-absor/logos/absor/v1/complete.svg'
            }
            alt="logo"
            style={{ maxWidth: '200px' }}
          />
        </Box>

        <Typography
          variant="h5"
          align="center"
          sx={{ mb: 0.5, fontWeight: 700, color: 'text.primary' }}
        >
          {process.env.REACT_APP_SITENAME}
        </Typography>
        <Typography
          variant="body2"
          align="center"
          sx={{ mb: 3, color: 'text.secondary' }}
        >
          Inicia sesión para continuar
        </Typography>

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label="Email"
            name="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={handleInputEmailChange}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label="Contraseña"
            type="password"
            id="password"
            autoComplete="current-password"
            value={password}
            onChange={handleInputPasswordChange}
          />
          {error && (
            <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
              {error}
            </Alert>
          )}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={loginLocked}
            sx={{ mt: 3, mb: 1, py: 1.4, fontSize: '1rem' }}
          >
            {loginLocked ? 'Entrando…' : 'Iniciar sesión'}
          </Button>
          <Button
            onClick={handleOpenModal}
            fullWidth
            sx={{ color: 'text.secondary', fontSize: '0.85rem' }}
          >
            ¿Olvidaste tu contraseña?
          </Button>
        </Box>
      </Paper>

      <Modal open={modalIsOpen} onClose={handleCloseModal}>
        <Paper
          elevation={8}
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: 460 },
            p: 4,
            borderRadius: 4,
            border: isDark
              ? '1px solid rgba(255,255,255,0.07)'
              : '1px solid rgba(0,7,255,0.08)',
          }}
        >
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
            Recuperar contraseña
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            Introduce tu dirección de correo electrónico y te enviaremos un enlace para restablecer tu contraseña.
          </Typography>
          <TextField
            margin="normal"
            required
            fullWidth
            id="reset-email"
            label="Email"
            name="reset-email"
            autoComplete="email"
            value={recoveryEmail}
            onChange={handleInputRecoveryEmailChange}
          />
          {errorResetPassword && (
            <Alert severity="error" sx={{ mt: 1.5, borderRadius: 2 }}>
              {errorResetPassword}
            </Alert>
          )}
          {successResetPassword && (
            <Alert severity="success" sx={{ mt: 1.5, borderRadius: 2 }}>
              {successResetPassword}
            </Alert>
          )}
          <Button
            variant="contained"
            fullWidth
            onClick={handleResetPassword}
            disabled={resetPasswordLocked}
            sx={{ mt: 2.5 }}
          >
            {resetPasswordLocked ? 'Enviando…' : 'Restablecer contraseña'}
          </Button>
          <Button
            fullWidth
            onClick={handleCloseModal}
            sx={{ mt: 1, color: 'text.secondary' }}
          >
            Cerrar
          </Button>
        </Paper>
      </Modal>
    </Box>
  );
}

export default Login;
